import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Sparkles, Plus, Calendar, LogOut, Loader2, BookOpen, Crown, Flame, Heart, Trash2, X, Check } from 'lucide-react';
import ThemeToggle from '../components/ThemeToggle';
import SiteFooter from '../components/SiteFooter';
import { format } from 'date-fns';
import { toast } from 'sonner';

const Dashboard = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [readings, setReadings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [streak, setStreak] = useState({ streak: 0, longest_streak: 0 });
  const [subscription, setSubscription] = useState({ is_premium: false });
  const [filter, setFilter] = useState('all'); // all | favorites
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deletingBulk, setDeletingBulk] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [confirmBulk, setConfirmBulk] = useState(false);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const [readingsRes, streakRes, subRes] = await Promise.all([
        axios.get(`${API}/readings`),
        axios.get(`${API}/streak`),
        axios.get(`${API}/me/subscription`),
      ]);
      setReadings(readingsRes.data.readings);
      setStreak(streakRes.data);
      setSubscription(subRes.data);
    } catch (error) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    onLogout();
    navigate('/');
  };

  const toggleFavorite = async (e, readingId) => {
    e.stopPropagation();
    try {
      const response = await axios.post(`${API}/readings/${readingId}/favorite`);
      setReadings((prev) =>
        prev.map((r) => (r.id === readingId ? { ...r, is_favorite: response.data.is_favorite } : r))
      );
    } catch (error) {
      toast.error('Failed to update favorite');
    }
  };

  const toggleSelect = (e, readingId) => {
    e.stopPropagation();
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(readingId)) next.delete(readingId);
      else next.add(readingId);
      return next;
    });
  };

  const enterSelectMode = () => {
    setSelectMode(true);
    setSelectedIds(new Set());
  };

  const exitSelectMode = () => {
    setSelectMode(false);
    setSelectedIds(new Set());
  };

  const selectAll = () => {
    setSelectedIds(new Set(filteredReadings.map((r) => r.id)));
  };

  const deleteOne = async (readingId) => {
    try {
      await axios.delete(`${API}/readings/${readingId}`);
      setReadings((prev) => prev.filter((r) => r.id !== readingId));
      toast.success('Reading deleted');
    } catch (error) {
      toast.error('Failed to delete reading');
    } finally {
      setConfirmDeleteId(null);
    }
  };

  const deleteBulk = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setDeletingBulk(true);
    try {
      const response = await axios.post(`${API}/readings/bulk-delete`, { ids });
      setReadings((prev) => prev.filter((r) => !selectedIds.has(r.id)));
      toast.success(`Deleted ${response.data.deleted_count} reading${response.data.deleted_count === 1 ? '' : 's'}`);
      exitSelectMode();
    } catch (error) {
      toast.error('Failed to delete selected');
    } finally {
      setDeletingBulk(false);
      setConfirmBulk(false);
    }
  };

  const filteredReadings = filter === 'favorites' ? readings.filter((r) => r.is_favorite) : readings;

  const getSpreadLabel = (type) => ({
    'single': 'Single Card',
    'three-card': 'Three Card',
    'five-card': 'Five Card',
    'celtic-cross': 'Celtic Cross',
    'daily': 'Daily Reading',
  }[type] || type);

  return (
    <div className="min-h-screen bg-[#050505] text-[#F8F9FA]">
      <nav className="glass-effect border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-[#D4AF37]" strokeWidth={1.5} />
            <span className="text-2xl font-heading font-light tracking-tight">Seatask Arcane AI</span>
          </div>
          <div className="flex items-center gap-2 sm:gap-4">
            <ThemeToggle />
            <button
              onClick={() => navigate('/encyclopedia')}
              data-testid="nav-encyclopedia"
              className="hidden sm:flex items-center gap-2 px-3 py-2 text-zinc-300 hover:text-[#D4AF37] transition-colors text-sm"
            >
              <BookOpen className="w-4 h-4" strokeWidth={1.5} />
              <span>Encyclopedia</span>
            </button>
            {subscription.is_premium ? (
              <span className="flex items-center gap-1 px-3 py-1 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full">
                <Crown className="w-3.5 h-3.5 text-[#D4AF37]" strokeWidth={1.5} />
                <span className="text-xs text-[#D4AF37] tracking-wide">Credits Ready</span>
              </span>
            ) : (
              <button
                onClick={() => navigate('/premium')}
                data-testid="nav-upgrade"
                className="flex items-center gap-1 px-3 py-1.5 bg-[#D4AF37] text-[#050505] hover:bg-[#F3E5AB] rounded-md text-xs font-medium transition-all"
              >
                <Crown className="w-3.5 h-3.5" strokeWidth={1.5} />
                Buy
              </button>
            )}
            <span className="hidden md:inline text-sm text-zinc-400 truncate max-w-[140px]">{user?.email}</span>
            <button
              onClick={handleLogout}
              data-testid="logout-button"
              className="p-2 text-zinc-400 hover:text-[#D4AF37] transition-colors"
            >
              <LogOut className="w-5 h-5" strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-12" data-testid="dashboard">
        <div className="mb-12 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <h1 className="text-5xl font-heading font-light tracking-tighter mb-4">Your Readings</h1>
            <p className="text-zinc-400 text-lg">Explore your mystical journey through the cards</p>
          </div>
          <div className="glass-effect rounded-lg px-6 py-4 flex items-center gap-4" data-testid="streak-display">
            <Flame className={`w-8 h-8 ${streak.streak > 0 ? 'text-[#D4AF37]' : 'text-zinc-600'}`} strokeWidth={1.5} />
            <div>
              <p className="text-xs tracking-[0.2em] uppercase text-zinc-500">Daily Streak</p>
              <p className="text-2xl font-heading font-medium">
                {streak.streak} <span className="text-sm text-zinc-500">days</span>
              </p>
              {streak.longest_streak > streak.streak && (
                <p className="text-xs text-zinc-500">Best: {streak.longest_streak}</p>
              )}
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mb-12">
          <button
            onClick={() => navigate('/reading/daily')}
            data-testid="daily-reading-card"
            className="glass-effect rounded-lg p-8 text-left card-hover group"
          >
            <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center group-hover:bg-[#D4AF37]/20 transition-colors mb-4">
              <Calendar className="w-6 h-6 text-[#D4AF37]" strokeWidth={1.5} />
            </div>
            <h3 className="text-2xl font-heading font-medium mb-2">Daily Reading</h3>
            <p className="text-zinc-400">Discover today's guidance and energy</p>
          </button>

          <button
            onClick={() => navigate('/reading/new')}
            data-testid="new-reading-card"
            className="glass-effect rounded-lg p-8 text-left card-hover group border-[#D4AF37]/30"
          >
            <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center group-hover:bg-[#D4AF37]/20 transition-colors mb-4">
              <Plus className="w-6 h-6 text-[#D4AF37]" strokeWidth={1.5} />
            </div>
            <h3 className="text-2xl font-heading font-medium mb-2">New Reading</h3>
            <p className="text-zinc-400">Create a personalized tarot reading</p>
          </button>

          <button
            onClick={() => navigate('/encyclopedia')}
            data-testid="encyclopedia-card"
            className="glass-effect rounded-lg p-8 text-left card-hover group"
          >
            <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center group-hover:bg-[#D4AF37]/20 transition-colors mb-4">
              <BookOpen className="w-6 h-6 text-[#D4AF37]" strokeWidth={1.5} />
            </div>
            <h3 className="text-2xl font-heading font-medium mb-2">Encyclopedia</h3>
            <p className="text-zinc-400">Explore the meaning of all 78 cards</p>
          </button>
        </div>

        <div>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-3">
            <h2 className="text-3xl font-heading font-medium">Reading History</h2>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setFilter('all')}
                data-testid="filter-all"
                className={`px-4 py-1.5 rounded-md text-sm transition-all ${
                  filter === 'all' ? 'bg-[#D4AF37] text-[#050505]' : 'border border-white/10 text-zinc-300 hover:border-[#D4AF37]/50'
                }`}
              >
                All
              </button>
              <button
                onClick={() => setFilter('favorites')}
                data-testid="filter-favorites"
                className={`px-4 py-1.5 rounded-md text-sm transition-all flex items-center gap-1.5 ${
                  filter === 'favorites' ? 'bg-[#D4AF37] text-[#050505]' : 'border border-white/10 text-zinc-300 hover:border-[#D4AF37]/50'
                }`}
              >
                <Heart className="w-3.5 h-3.5" strokeWidth={1.5} />
                Favorites
              </button>
              {!selectMode ? (
                readings.length > 0 && (
                  <button
                    onClick={enterSelectMode}
                    data-testid="enter-select-mode"
                    className="px-4 py-1.5 rounded-md text-sm border border-white/10 text-zinc-300 hover:border-red-500/50 hover:text-red-400 transition-all flex items-center gap-1.5"
                  >
                    <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                    Select
                  </button>
                )
              ) : (
                <>
                  <button
                    onClick={selectAll}
                    data-testid="select-all"
                    className="px-4 py-1.5 rounded-md text-sm border border-white/10 text-zinc-300 hover:border-[#D4AF37]/50 transition-all"
                  >
                    Select all
                  </button>
                  <button
                    onClick={exitSelectMode}
                    data-testid="exit-select-mode"
                    className="px-4 py-1.5 rounded-md text-sm border border-white/10 text-zinc-300 hover:text-[#D4AF37] transition-all flex items-center gap-1.5"
                  >
                    <X className="w-3.5 h-3.5" strokeWidth={1.5} />
                    Cancel
                  </button>
                </>
              )}
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
            </div>
          ) : filteredReadings.length === 0 ? (
            <div className="glass-effect rounded-lg p-12 text-center" data-testid="no-readings-message">
              <p className="text-zinc-400 mb-4">
                {filter === 'favorites' ? 'No favorites yet — tap the heart on any reading to save it.' : 'No readings yet'}
              </p>
              {filter !== 'favorites' && (
                <button
                  onClick={() => navigate('/reading/new')}
                  className="text-[#D4AF37] hover:text-[#F3E5AB] transition-colors"
                >
                  Create your first reading →
                </button>
              )}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredReadings.map((reading) => {
                const isSelected = selectedIds.has(reading.id);
                const onCardClick = selectMode
                  ? (e) => toggleSelect(e, reading.id)
                  : () => navigate(`/reading/${reading.id}`);
                return (
                  <div
                    key={reading.id}
                    onClick={onCardClick}
                    data-testid={`reading-card-${reading.id}`}
                    className={`glass-effect rounded-lg p-6 text-left card-hover relative cursor-pointer transition-all ${
                      isSelected ? 'ring-2 ring-[#D4AF37]' : ''
                    }`}
                  >
                    {selectMode && (
                      <div
                        className={`absolute top-4 left-4 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                          isSelected ? 'bg-[#D4AF37] border-[#D4AF37]' : 'bg-transparent border-white/30'
                        }`}
                      >
                        {isSelected && <Check className="w-4 h-4 text-[#050505]" strokeWidth={3} />}
                      </div>
                    )}

                    {!selectMode && (
                      <div className="absolute top-3 right-3 flex gap-1">
                        <button
                          onClick={(e) => toggleFavorite(e, reading.id)}
                          data-testid={`favorite-toggle-${reading.id}`}
                          className="p-1.5 rounded hover:bg-white/5 hover:scale-110 transition-all"
                          title={reading.is_favorite ? 'Remove from favorites' : 'Add to favorites'}
                        >
                          <Heart
                            className={`w-4 h-4 ${reading.is_favorite ? 'fill-[#D4AF37] text-[#D4AF37]' : 'text-zinc-500'}`}
                            strokeWidth={1.5}
                          />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setConfirmDeleteId(reading.id);
                          }}
                          data-testid={`delete-reading-${reading.id}`}
                          className="p-1.5 rounded hover:bg-red-500/10 transition-all"
                          title="Delete reading"
                        >
                          <Trash2 className="w-4 h-4 text-zinc-500 hover:text-red-400 transition-colors" strokeWidth={1.5} />
                        </button>
                      </div>
                    )}

                    <div className={`flex items-center justify-between mb-3 ${selectMode ? 'pl-9' : 'pr-16'}`}>
                      <span className="text-xs tracking-[0.2em] uppercase text-[#D4AF37]">
                        {getSpreadLabel(reading.spread_type)}
                      </span>
                      <span className="text-xs text-zinc-500">
                        {format(new Date(reading.created_at), 'MMM d, yyyy')}
                      </span>
                    </div>
                    <p className={`text-zinc-300 text-sm line-clamp-2 mb-3 ${selectMode ? 'pl-9' : ''}`}>
                      {reading.question || 'General Reading'}
                    </p>
                    <div className={`flex gap-1 ${selectMode ? 'pl-9' : ''}`}>
                      {reading.cards.slice(0, 3).map((card, idx) => (
                          <div
                            key={idx}
                            className="w-8 aspect-[7/12] rounded overflow-hidden bg-[#0a0a14] relative"
                          >
                            {card.image_url && (
                              <img
                                src={card.image_url}
                                alt={card.name}
                                className="w-full h-full object-contain"
                              />
                            )}
                          </div>
                      ))}
                      {reading.cards.length > 3 && (
                        <span className="text-xs text-zinc-500 self-center ml-1">+{reading.cards.length - 3}</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Floating bulk-delete bar */}
      {selectMode && selectedIds.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 glass-effect rounded-full px-6 py-3 flex items-center gap-4 shadow-2xl border border-[#D4AF37]/30" data-testid="bulk-delete-bar">
          <span className="text-sm text-zinc-300">
            <span className="text-[#D4AF37] font-medium">{selectedIds.size}</span> selected
          </span>
          <button
            onClick={() => setConfirmBulk(true)}
            data-testid="bulk-delete-button"
            disabled={deletingBulk}
            className="flex items-center gap-2 px-4 py-1.5 bg-red-500/90 text-white rounded-full hover:bg-red-500 transition-all text-sm font-medium disabled:opacity-50"
          >
            <Trash2 className="w-4 h-4" strokeWidth={1.5} />
            Delete selected
          </button>
        </div>
      )}

      {/* Single delete confirmation */}
      {confirmDeleteId && (
        <ConfirmDialog
          title="Delete this reading?"
          message="This action cannot be undone."
          onCancel={() => setConfirmDeleteId(null)}
          onConfirm={() => deleteOne(confirmDeleteId)}
          testId="confirm-single-delete"
        />
      )}

      {/* Bulk delete confirmation */}
      {confirmBulk && (
        <ConfirmDialog
          title={`Delete ${selectedIds.size} reading${selectedIds.size === 1 ? '' : 's'}?`}
          message="These readings will be permanently removed from your history."
          onCancel={() => setConfirmBulk(false)}
          onConfirm={deleteBulk}
          confirming={deletingBulk}
          testId="confirm-bulk-delete"
        />
      )}
      <SiteFooter />
    </div>
  );
};

// Inline confirmation dialog
const ConfirmDialog = ({ title, message, onCancel, onConfirm, confirming, testId }) => (
  <div
    className="fixed inset-0 z-50 bg-[#050505]/80 backdrop-blur-md flex items-center justify-center p-4"
    onClick={onCancel}
    data-testid={testId}
  >
    <div
      className="glass-effect rounded-lg max-w-md w-full p-8"
      onClick={(e) => e.stopPropagation()}
    >
      <h3 className="text-2xl font-heading font-medium mb-3">{title}</h3>
      <p className="text-zinc-400 text-sm mb-6">{message}</p>
      <div className="flex gap-3 justify-end">
        <button
          onClick={onCancel}
          data-testid="confirm-cancel"
          className="px-5 py-2 border border-white/10 text-zinc-300 rounded-md hover:border-[#D4AF37]/50 transition-all text-sm"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          disabled={confirming}
          data-testid="confirm-delete"
          className="px-5 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-all text-sm font-medium disabled:opacity-50 flex items-center gap-2"
        >
          {confirming && <Loader2 className="w-4 h-4 animate-spin" />}
          Delete
        </button>
      </div>
    </div>
  </div>
);

export default Dashboard;
